import { useEffect, useRef, useState } from 'react';
import {
  Title, Stack, Card, Text, Button, Group, Alert, Tabs,
  TextInput, Textarea, Image, Box, Loader, FileButton, NumberInput,
} from '@mantine/core';
import {
  IconDownload, IconUpload, IconAlertCircle, IconCheck,
  IconDatabase, IconFileInvoice, IconBuilding, IconBuildingBank, IconCreditCard,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import HttpClient from '../helpers/HttpClient';
import { getAccessToken } from '../helpers/TokenRefresh';
import { InvoiceSetting } from '../types/invoice-setting.type';
import PaymentAccountList from './PaymentAccountList';

type Status = { type: 'success' | 'error'; message: string } | null;

const client = new HttpClient<InvoiceSetting>('/invoice-setting');

export default function Admin() {
  // ── Database tab ──────────────────────────────────────────────────────────
  const [restoreStatus, setRestoreStatus] = useState<Status>(null);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleBackup = async () => {
    const token = await getAccessToken();
    const url = `${import.meta.env.VITE_X_API_URL || ''}/admin/backup`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Backup request failed');
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : 'backup.db';
        return res.blob().then(blob => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {});
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.name.endsWith('.db') && !file.name.endsWith('.sqlite')) {
      setRestoreStatus({ type: 'error', message: 'Please select a valid .db or .sqlite file.' });
      return;
    }

    const confirmed = window.confirm(
      `Restore database from "${file.name}"?\n\nThis will overwrite the current database. The server will need to be restarted afterwards.`
    );
    if (!confirmed) return;

    setRestoring(true);
    setRestoreStatus(null);
    try {
      const token = await getAccessToken();
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${import.meta.env.VITE_X_API_URL || ''}/admin/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (res.ok) {
        setRestoreStatus({ type: 'success', message: data.message });
      } else {
        setRestoreStatus({ type: 'error', message: data.message || 'Restore failed' });
      }
    } catch {
      setRestoreStatus({ type: 'error', message: 'Network error during restore' });
    } finally {
      setRestoring(false);
    }
  };

  // ── Invoice + Club settings ───────────────────────────────────────────────
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState<InvoiceSetting>({
    id: 1,
    senderAddressLine: '',
    footerLeft: '',
    footerCenter: '',
    footerRight: '',
    clubName: '',
    logoBase64: '',
  });

  useEffect(() => {
    client.getSingle().then(data => {
      if (data) setSettings(data);
    }).finally(() => setLoadingSettings(false));
  }, []);

  const handleSaveSettings = async (fields: Partial<InvoiceSetting>) => {
    setSavingSettings(true);
    try {
      const updated = await client.put({ ...settings, ...fields });
      if (updated?.data) setSettings(updated.data as InvoiceSetting);
      notifications.show({ message: 'Gespeichert', color: 'green' });
    } catch {
      notifications.show({ message: 'Speichern fehlgeschlagen', color: 'red' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleLogoFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setSettings(s => ({ ...s, logoBase64: base64 }));
    };
    reader.readAsDataURL(file);
  };

  if (loadingSettings) return <Loader mt="xl" />;

  return (
    <Stack>
      <Title order={2}>Admin</Title>

      <Tabs defaultValue="database">
        <Tabs.List>
          <Tabs.Tab value="database" leftSection={<IconDatabase size={16} />}>Datenbank</Tabs.Tab>
          <Tabs.Tab value="invoice" leftSection={<IconFileInvoice size={16} />}>Rechnung</Tabs.Tab>
          <Tabs.Tab value="club" leftSection={<IconBuilding size={16} />}>Verein</Tabs.Tab>
          <Tabs.Tab value="bank" leftSection={<IconBuildingBank size={16} />}>Bank</Tabs.Tab>
          <Tabs.Tab value="paymentAccounts" leftSection={<IconCreditCard size={16} />}>Zahlungskonten</Tabs.Tab>
        </Tabs.List>

        {/* ── Database ── */}
        <Tabs.Panel value="database" pt="md">
          <Stack>
            <Card withBorder shadow="sm" radius="md" p="lg">
              <Stack gap="xs">
                <Text fw={600} size="lg">Backup herunterladen</Text>
                <Text size="sm" c="dimmed">Aktuelle SQLite-Datenbank als Datei herunterladen.</Text>
                <Group>
                  <Button leftSection={<IconDownload size={16} />} onClick={handleBackup}>
                    Backup herunterladen
                  </Button>
                </Group>
              </Stack>
            </Card>

            <Card withBorder shadow="sm" radius="md" p="lg">
              <Stack gap="xs">
                <Text fw={600} size="lg">Datenbank wiederherstellen</Text>
                <Text size="sm" c="dimmed">
                  Backup hochladen und einspielen. Der Server muss danach neu gestartet werden.
                </Text>
                <Group>
                  <Button
                    leftSection={<IconUpload size={16} />}
                    onClick={() => fileRef.current?.click()}
                    loading={restoring}
                    color="orange"
                  >
                    Backup hochladen
                  </Button>
                </Group>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".db,.sqlite"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                {restoreStatus && (
                  <Alert
                    icon={restoreStatus.type === 'success' ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
                    color={restoreStatus.type === 'success' ? 'green' : 'red'}
                  >
                    {restoreStatus.message}
                  </Alert>
                )}
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* ── Invoice ── */}
        <Tabs.Panel value="invoice" pt="md">
          <Stack maw={700}>
            <Textarea
              label="Absenderzeile"
              rows={2}
              value={settings.senderAddressLine}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, senderAddressLine: v })); }}
            />
            <Textarea
              label="Fußzeile links"
              rows={4}
              value={settings.footerLeft ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, footerLeft: v })); }}
            />
            <Textarea
              label="Fußzeile Mitte"
              rows={4}
              value={settings.footerCenter ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, footerCenter: v })); }}
            />
            <Textarea
              label="Fußzeile rechts"
              rows={4}
              value={settings.footerRight ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, footerRight: v })); }}
            />
            <TextInput
              label="ZVR-Zahl (ELDA L19 Export)"
              description="10-stellige ZVR-Zahl des Vereins"
              value={settings.zvrNumber ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, zvrNumber: v })); }}
            />
            <TextInput
              label="Steuernummer (ELDA L19 Export)"
              description="9-stellige Steuernummer (optional)"
              value={settings.steuerNummer ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, steuerNummer: v })); }}
            />
            <Group>
              <Button
                loading={savingSettings}
                onClick={() => handleSaveSettings({
                  senderAddressLine: settings.senderAddressLine,
                  footerLeft: settings.footerLeft,
                  footerCenter: settings.footerCenter,
                  footerRight: settings.footerRight,
                  zvrNumber: settings.zvrNumber,
                  steuerNummer: settings.steuerNummer,
                })}
              >
                Speichern
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* ── Club ── */}
        <Tabs.Panel value="club" pt="md">
          <Stack maw={500}>
            <TextInput
              label="Vereinsname"
              value={settings.clubName ?? ''}
              onChange={e => { const v = e.currentTarget.value; setSettings(s => ({ ...s, clubName: v })); }}
            />

            <Box>
              <Text size="sm" fw={500} mb={4}>Vereinslogo</Text>
              {settings.logoBase64 && (
                <Image
                  src={settings.logoBase64}
                  alt="Vereinslogo"
                  h={80}
                  w="auto"
                  fit="contain"
                  mb="xs"
                />
              )}
              <FileButton onChange={handleLogoFile} accept="image/png,image/jpeg,image/svg+xml,image/webp">
                {(props) => (
                  <Button {...props} variant="light" leftSection={<IconUpload size={16} />}>
                    Logo hochladen
                  </Button>
                )}
              </FileButton>
              {settings.logoBase64 && (
                <Button
                  variant="subtle"
                  color="red"
                  ml="xs"
                  onClick={() => setSettings(s => ({ ...s, logoBase64: '' }))}
                >
                  Entfernen
                </Button>
              )}
            </Box>

            <Group>
              <Button
                loading={savingSettings}
                onClick={() => handleSaveSettings({ clubName: settings.clubName, logoBase64: settings.logoBase64 })}
              >
                Speichern
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
        {/* ── Bank ── */}
        <Tabs.Panel value="bank" pt="md">
          <Stack maw={400}>
            <NumberInput
              label="SEPA-Maximalbetrag pro Monat (€)"
              description="Jede Zahlung im SEPA-Export wird auf diesen Betrag begrenzt. Leer lassen für kein Limit."
              value={settings.sepaMaxAmountPerMonth ?? ''}
              onChange={(v) => setSettings(s => ({ ...s, sepaMaxAmountPerMonth: v === '' ? undefined : Number(v) }))}
              min={0}
              decimalSeparator=","
              prefix="€ "
              placeholder="Kein Limit"
              allowDecimal
            />
            <Group>
              <Button
                loading={savingSettings}
                onClick={() => handleSaveSettings({ sepaMaxAmountPerMonth: settings.sepaMaxAmountPerMonth })}
              >
                Speichern
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* ── Payment accounts ── */}
        <Tabs.Panel value="paymentAccounts" pt="md">
          <PaymentAccountList />
        </Tabs.Panel>

      </Tabs>
    </Stack>
  );
}
